import type {
  LightingAdvisorQuestion,
  LightingAdvisorRecommendationsResponse,
  LightingAnswers
} from '~/types/lightingAdvisor';
import { getToolsApiLocaleParams } from '~/utils/tools/getToolsApiLocaleParams';
import {
  buildQueryFromAnswers,
  cascadeResetAnswers,
  getAllowedLightTypes,
  getFilteredQuestionInputs,
  getQuestionTooltipKeys,
  getWarningNotesForQuestion,
  isQuestionVisible,
  normalizeAnswersAfterLoad
} from '~/utils/lightingAdvisor/questionDependencies';
import { mapLightingResultsToMagentoFilters } from '~/utils/lightingAdvisor/mapResultsToMagentoFilters';
import { applyLightingAdvisorDictionary } from '~/utils/lightingAdvisor/questionsCatalog';
import {
  normalizeLightingAdvisorResponse,
  normalizeLightingAdvisorResults
} from '~/utils/lightingAdvisor/normalizeLightingAdvisorResponse';

export function useLightingAdvisor() {
  const { $locale, $shopStore, $textDictionary } = useNuxtApp();
  const analytics = useLightingAdvisorAnalytics();
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  const questions = ref<LightingAdvisorQuestion[]>([]);
  const lastResults = ref<
    LightingAdvisorRecommendationsResponse['results'] | null
  >(null);
  const recommendedProducts = ref<
    NonNullable<LightingAdvisorRecommendationsResponse['recommended_products']>
  >([]);

  const getLocaleParams = () => getToolsApiLocaleParams($locale.value);

  const fetchRecommendations = async (
    answers: LightingAnswers = {}
  ): Promise<LightingAdvisorRecommendationsResponse | null> => {
    isLoading.value = true;
    error.value = null;
    const locale = getLocaleParams();
    const answerQuery = buildQueryFromAnswers(answers, questions.value);

    try {
      const raw = await $fetch<unknown>('/api/tools/lighting/recommendations', {
        retry: 0,
        query: {
          lang: locale.lang,
          country: locale.country,
          locale: $locale.value,
          store: $shopStore.value,
          ...answerQuery
        }
      });
      const response = normalizeLightingAdvisorResponse(raw);
      if (!response) {
        error.value = 'lightingAdvisor.error.recommendationFailed';
        return null;
      }
      questions.value = applyLightingAdvisorDictionary(
        response.questions,
        ($textDictionary.value ?? {}) as Record<string, string | undefined>
      );
      lastResults.value = normalizeLightingAdvisorResults(response.results);
      recommendedProducts.value = response.recommended_products ?? [];
      return response;
    } catch {
      error.value = 'lightingAdvisor.error.recommendationFailed';
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  const loadQuestions =
    async (): Promise<LightingAdvisorRecommendationsResponse | null> => {
      return await fetchRecommendations({});
    };

  function useLightingQuestionFlow(answers: Ref<LightingAnswers>) {
    const normalizedAnswers = computed(() =>
      normalizeAnswersAfterLoad(answers.value)
    );

    const setAnswer = (questionId: string, value: string | null) => {
      const next = cascadeResetAnswers(
        { ...answers.value, [questionId]: value },
        questionId
      );
      answers.value = next;
    };

    const questionsForTile = (prefix: 'a2s1' | 'a2s2' | 'a2s3') =>
      computed(() =>
        questions.value.filter((question) => question.id.startsWith(prefix))
      );

    return {
      normalizedAnswers,
      setAnswer,
      questionsForTile,
      isQuestionVisible: (questionId: string) =>
        isQuestionVisible(questionId, answers.value),
      getFilteredInputs: (question: LightingAdvisorQuestion) =>
        getFilteredQuestionInputs(question, answers.value),
      getTooltipKeys: (question: LightingAdvisorQuestion) =>
        getQuestionTooltipKeys(question, {
          allowedLightTypes: getAllowedLightTypes(
            answers.value.a2s1q1,
            answers.value.a2s1q2
          ),
          a2s3q1: answers.value.a2s3q1
        }),
      getWarningNotes: (question: LightingAdvisorQuestion) =>
        getWarningNotesForQuestion(question, answers.value, {
          allowedLightTypes: getAllowedLightTypes(
            answers.value.a2s1q1,
            answers.value.a2s1q2
          ),
          a2s3q1: answers.value.a2s3q1
        }),
      mapResultsToMagentoFilters: () =>
        lastResults.value
          ? mapLightingResultsToMagentoFilters(lastResults.value)
          : {}
    };
  }

  return {
    isLoading: readonly(isLoading),
    error: readonly(error),
    questions: readonly(questions),
    lastResults: readonly(lastResults),
    recommendedProducts: readonly(recommendedProducts),
    getLocaleParams,
    fetchRecommendations,
    loadQuestions,
    trackEvent: analytics.trackEvent,
    useLightingQuestionFlow,
    buildQueryFromAnswers: (answers: LightingAnswers) =>
      buildQueryFromAnswers(answers, questions.value)
  };
}
